import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { frappePost } from '@/lib/frappe';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (filters?: Record<string, unknown>) => [...notificationKeys.all, 'list', filters] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

interface Notification {
  name: string;
  subject: string;
  message: string;
  notification_type: string;
  is_read: number;
  creation: string;
  reference_doctype?: string;
  reference_name?: string;
}

interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  unread_count: number;
}

export function useNotifications(filters?: {
  type?: string;
  is_read?: number;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: notificationKeys.list(filters),
    queryFn: () =>
      frappePost<NotificationListResponse>(
        'hudan_lms.api.notifications.get_my_notifications',
        {
          notification_type: filters?.type,
          is_read: filters?.is_read,
          page: filters?.page || 1,
          limit: filters?.limit || 20,
        }
      ),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () =>
      frappePost<{ count: number }>('hudan_lms.api.notifications.get_unread_count'),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Poll every minute
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationName: string) =>
      frappePost('hudan_lms.api.notifications.mark_read', { notification_name: notificationName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      frappePost('hudan_lms.api.notifications.mark_all_read'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
